import { createFileRoute, Link } from "@tanstack/react-router";
import { Bike, MapPin, Waves, Phone } from "lucide-react";
import { breadcrumbSchema, canonical, faqPageSchema } from "@/lib/seo";

export const Route = createFileRoute("/blog/exploring-crystal-beach-by-bike")({
  head: () => ({
    meta: [
      { title: "Exploring Crystal Beach by Bike - Malak BnB Resort" },
      { name: "description", content: "A cycling guide to Crystal Beach, Ontario: the town loop, the waterfront, and rides out toward Ridgeway and Fort Erie. Stay at Malak BnB Resort." },
      { property: "og:title", content: "Exploring Crystal Beach by Bike - Malak BnB Resort" },
      { property: "og:description", content: "Flat roads, a waterfront path, and easy rides toward Ridgeway and Fort Erie from a Crystal Beach base." },
      { property: "og:url", content: "https://malakbnbresort.ca/blog/exploring-crystal-beach-by-bike" },
      { property: "og:image", content: "/images/autumn-cycling.webp" },
      { "script:ld+json": faqPageSchema(FAQ) },
      {
        "script:ld+json": breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "Blog", path: "/blog" },
          { name: "Exploring Crystal Beach by Bike", path: "/blog/exploring-crystal-beach-by-bike" },
        ]),
      },
    ],
    links: [canonical("/blog/exploring-crystal-beach-by-bike")],
  }),
  component: CyclingGuidePage,
});

const FAQ = [
  {
    q: "Is Crystal Beach easy to cycle around?",
    a: "Yes. Crystal Beach is flat and small, with quiet residential streets and a lakefront that is easy to reach from almost anywhere in town. Most of what there is to see is within a 20 minute ride.",
  },
  {
    q: "What cycling routes connect to Crystal Beach?",
    a: "Crystal Beach is close to Cycle and Stay Niagara routes, with rides extending toward Ridgeway and Fort Erie. The terrain is flat along the lake and stays manageable further inland.",
  },
  {
    q: "Is Malak BnB Resort a good place to stay for a cycling day trip?",
    a: "Malak BnB Resort is five minutes from Bay Beach and sits in the middle of the Crystal Beach loop described in this guide. Breakfast is included, and the outdoor heated pool is a good way to finish a ride.",
  },
  {
    q: "How do I request a room at Malak BnB Resort for a cycling trip?",
    a: "Submit a reservation request through the Malak BnB Resort booking form with your dates. The team personally reviews and confirms availability before sending payment details.",
  },
];

function CyclingGuidePage() {
  return (
    <div>
      {/* Hero */}
      <section className="relative h-[55vh] min-h-[380px] overflow-hidden">
        <img
          src="/images/autumn-cycling.webp"
          alt="Cyclist riding a tree-lined path in fall"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground/65 via-foreground/20 to-transparent" />
        <div className="relative z-10 flex h-full items-end pb-16 px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-7xl w-full">
            <div className="flex items-center gap-2 text-white/70 text-sm mb-3">
              <Link to="/blog" className="hover:text-white transition-colors">Blog</Link>
              <span>/</span>
              <span>Exploring Crystal Beach by Bike</span>
            </div>
            <p className="text-white/70 font-semibold text-sm uppercase tracking-widest mb-3">A Cycling Guide</p>
            <h1 className="text-4xl sm:text-5xl font-heading text-white leading-tight max-w-2xl">
              Exploring Crystal Beach by Bike
            </h1>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="py-20">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg text-muted-foreground space-y-5 leading-relaxed">
            <p>
              Crystal Beach is small enough to see almost entirely by bike in a single afternoon, and flat enough
              that it does not ask much of you along the way. This is not a mountain-route kind of place. It is a
              town built for a slow ride: lake on one side, quiet streets on the other, and nowhere far enough away
              to make the trip feel like an effort.
            </p>
            <p>
              Here are the rides worth doing, from a short loop around town to longer stretches out toward Ridgeway
              and Fort Erie.
            </p>
          </div>
        </div>
      </section>

      {/* Town loop */}
      <section className="pb-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="rounded-2xl overflow-hidden shadow-xl order-2 lg:order-1">
              <img src="/images/explore-biking.webp" alt="Cycling through the streets of Crystal Beach, Ontario" className="w-full object-cover aspect-[4/3]" loading="lazy" />
            </div>
            <div className="order-1 lg:order-2">
              <div className="flex items-center gap-2 mb-3">
                <Bike className="h-5 w-5 text-primary" />
                <p className="text-primary font-semibold text-sm uppercase tracking-wider">The Town Loop</p>
              </div>
              <h2 className="text-2xl sm:text-3xl font-heading text-foreground leading-snug mb-4">
                A Short Ride That Covers the Whole Town
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                Start near Bay Beach and ride the residential streets inland before circling back to the water.
                Clapboard cottages, small front gardens, and a handful of cafes and shops make it worth doing at an
                easy pace rather than rushing through. Twenty minutes covers most of it.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Waterfront */}
      <section className="py-20 bg-card">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Waves className="h-5 w-5 text-primary" />
                <p className="text-primary font-semibold text-sm uppercase tracking-wider">The Waterfront</p>
              </div>
              <h2 className="text-2xl sm:text-3xl font-heading text-foreground leading-snug mb-4">
                Riding Along Lake Erie
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                From Bay Beach, the shoreline stretches toward Ridge Beach with the lake visible for most of the
                ride. It is flatter and quieter than the town streets, and the kind of stretch worth doing slowly,
                stopping wherever the view asks you to.
              </p>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-xl">
              <img src="/images/explore-ridge-beach.webp" alt="Ridge Beach shoreline near Crystal Beach on Lake Erie" className="w-full object-cover aspect-[4/3]" loading="lazy" />
            </div>
          </div>
        </div>
      </section>

      {/* Further afield */}
      <section className="py-20">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex items-center justify-center gap-2 mb-3">
            <MapPin className="h-5 w-5 text-primary" />
            <p className="text-primary font-semibold text-sm uppercase tracking-wider">Further Afield</p>
          </div>
          <h2 className="text-3xl font-heading text-foreground mb-6">Toward Ridgeway and Fort Erie</h2>
          <p className="text-muted-foreground leading-relaxed">
            For a longer day, the flat roads connecting Crystal Beach to Ridgeway and Fort Erie link into the wider
            Cycle and Stay Niagara network. Neither town is far, and both are worth a stop for coffee or lunch
            before the ride back. Riders looking for a base built around this kind of trip can read more about{" "}
            <Link to="/cycling-friendly-accommodation-niagara" className="text-primary font-semibold hover:underline">
              cycling-friendly accommodation in the Niagara Region
            </Link>
            .
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-card">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-primary font-semibold text-sm uppercase tracking-wider mb-3">Common Questions</p>
            <h2 className="text-3xl font-heading text-foreground">Cycling in Crystal Beach</h2>
          </div>
          <div className="space-y-8">
            {FAQ.map(({ q, a }) => (
              <div key={q} className="border-b border-border pb-8 last:border-0 last:pb-0">
                <h3 className="font-heading text-lg text-foreground mb-3">{q}</h3>
                <p className="text-muted-foreground leading-relaxed text-sm">{a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-foreground">
        <div className="mx-auto max-w-3xl px-4 text-center">
          <p className="text-primary-foreground/50 font-semibold text-sm uppercase tracking-widest mb-4">Crystal Beach, Ontario</p>
          <h2 className="text-3xl sm:text-4xl font-heading text-primary-foreground leading-tight">
            Make Malak BnB Resort your ride day base
          </h2>
          <p className="mt-5 text-primary-foreground/70 max-w-lg mx-auto leading-relaxed">
            Submit a reservation request and the team will personally confirm availability and send payment details directly.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link to="/book" className="inline-flex items-center justify-center rounded-full bg-primary px-10 py-4 text-sm font-semibold text-primary-foreground hover:bg-primary/90 transition-colors shadow-lg">
              Request to Book
            </Link>
            <a href="tel:+12899298055" className="inline-flex items-center gap-2 text-primary-foreground/70 hover:text-primary-foreground text-sm font-medium transition-colors">
              <Phone className="h-4 w-4" />
              1-289-929-8055
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
